import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import companyLogoSrc from '@assets/images/logo/logo.png';

import {
    useCurrentStudyChatStatus,
    useIsChatSidebarVisibleOnXs,
    useMethods as useChatClientMethods,
    useTotalNumberOfUnreadEvents,
} from '$app-web/components/chat/state/chat-client.state';
import Loading from '$app-web/components/loading';

import { CATEGORY_TYPES } from './constants';
import Content from './content';
import Sidebar from './sidebar';
import classes from './styles.module.scss';

const Chat = (props) => {
    const { t } = useTranslation();
    const lastNotificationTime = useRef(0);
    const isDocumentVisible = useRef(true);
    const { currentStudyId, studyUsersDataLoader, category, categoryOnChange, roomId, roomOnClick } = props;
    const chatClientMethods = useChatClientMethods();
    const chatStatus = useCurrentStudyChatStatus();
    const isChatSidebarVisibleOnXs = useIsChatSidebarVisibleOnXs();
    const numberOfEventsByRoom = useTotalNumberOfUnreadEvents();
    const totalNumberOfEvents = Object.values(numberOfEventsByRoom).reduce((acc, val) => acc + val, 0);
    const lastTotalNumberOfEvents = useRef(totalNumberOfEvents);

    useEffect(() => {
        chatClientMethods.setCurrentStudyId(currentStudyId);
    }, [chatClientMethods, currentStudyId]);

    useEffect(() => {
        chatClientMethods.setCurrentStudyUsersDataLoader(studyUsersDataLoader);
    }, [chatClientMethods, studyUsersDataLoader]);

    useEffect(() => {
        chatClientMethods.setRoomOnClick(roomOnClick);
    }, [chatClientMethods, roomOnClick]);

    useEffect(() => {
        chatClientMethods.setSelectedRoomId(roomId);
    }, [chatClientMethods, roomId]);

    useEffect(() => {
        function handleVisibilityChange() {
            isDocumentVisible.current = document.visibilityState === 'visible';
        }

        if (window.Notification) {
            window.Notification.requestPermission().then((permission) => {
                if (permission === 'granted') {
                    document.addEventListener('visibilitychange', handleVisibilityChange);
                }
            });
        }

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const handleRoomOnClick = (...params) => {
        roomOnClick(...params);
        chatClientMethods.setIsChatSidebarVisibleOnXs(false);
    };

    useEffect(() => {
        const isPageHidden = !isDocumentVisible.current;
        const isNotificationsDelayPassed = Date.now() - lastNotificationTime.current > 30_000;
        const hasNewMessages = totalNumberOfEvents > lastTotalNumberOfEvents.current;

        if (isPageHidden && isNotificationsDelayPassed && hasNewMessages) {
            lastTotalNumberOfEvents.current = totalNumberOfEvents;
            lastNotificationTime.current = Date.now();

            // Shared by RDash (`rdash` ns) and standalone PDash chat (`pdash` ns); fall back so
            // the label resolves in whichever namespace is loaded instead of showing the raw key.
            new Notification(t(['rdash:avicenna_research', 'pdash:avicenna_research']), {
                body: t(['rdash:you_have_new_message', 'pdash:you_have_new_message']),
                icon: companyLogoSrc,
            });
        } else {
            lastTotalNumberOfEvents.current = totalNumberOfEvents;
        }
    }, [t, totalNumberOfEvents]);

    /*
        TODO: use chatClientStatus.isConnectionLost and chatClientStatus.isReconnecting for showing an overlay with different
         message and a loading indicator that disables user interaction with the chat.
     */

    return (
        <div className={clsx('flex', classes.wrapper)}>
            <div className={clsx('item md-auto', classes.sidebar, isChatSidebarVisibleOnXs ? 'xs-fill' : 'xs-0')}>
                <Sidebar
                    category={category}
                    categoryOnChange={categoryOnChange}
                    currentRoomId={roomId}
                    roomOnClick={handleRoomOnClick}
                />
            </div>
            <div className={clsx('item md-fill', classes.content, isChatSidebarVisibleOnXs ? 'xs-0' : 'xs-fill')}>
                <Content key={roomId} roomId={roomId} />
            </div>
            <Loading
                loading={!chatStatus.isReady && !chatStatus.isFailed}
                error={chatStatus.isFailed}
                variant="parent-overlay"
                errorHasButton
            />
        </div>
    );
};

Chat.defaultProps = {
    roomId: undefined,
};

Chat.propTypes = {
    currentStudyId: PropTypes.number.isRequired,
    studyUsersDataLoader: PropTypes.func.isRequired,
    category: PropTypes.oneOf(Object.values(CATEGORY_TYPES)).isRequired,
    categoryOnChange: PropTypes.func.isRequired,
    roomOnClick: PropTypes.func.isRequired,
    roomId: PropTypes.string,
};

export default Chat;
